import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import DoughnutChart from 'components/DoughnutChart'
import pick from 'lodash/pick'
import { getReviewAnalytics } from 'services/apiKYP'
import { Alert, Container, Grid, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import LoadingComponent from 'components/LoadingComponent'
import Colors, { chartColors } from 'utils/Colors'
import { Button } from 'components'
import {
  ApplicationApprovalByRiskChart,
  ApplicationApprovalChart,
  ApplicationStatsSummary,
  AssessorsResponseTimeSLAChart,
  ComplianceAnalysisSLAChart,
  DashboardCard,
  ReviewStatsSummary,
} from 'components/Statistics'

const Statistics = () => {
  const token = useSelector((state) => state.auth.access_token)

  const [analysisData, setAnalysisData] = useState(
    {
      sla_analytics: {
        average_analysis_time: 0,
        min_analysis_time: 0,
        max_analysis_time: 0,
      },
      review_stats: {
        created: 0,
        pending: 0,
        forwarded: 0,
        suspended: 0,
        finished: {
          total: 0,
          approved: 0,
          reproved: 0,
        },
      },
      application_stats: {
        approved: {
          total: 0,
          manual: 0,
          auto: 0,
        },
        reproved: {
          total: 0,
          manual: 0,
          auto: 0,
        },
        created: 0,
        finished: 0,
        expired: 0,
        dd_in_progress: 0,
        processing_bgc: 0,
        waiting_review: 0,
      },
    },
  )

  const currentDate = new Date()
  const oneMonthAgo = new Date(currentDate)
  oneMonthAgo.setMonth(currentDate.getMonth() - 1)

  const initialValues = {
    startDate: oneMonthAgo.toISOString().slice(0, 10),
    endDate: currentDate.toISOString().slice(0, 10),
  }

  const [analysisStatus, setAnalysisStatus] = useState([])
  const [finishedStatus, setFinishedStatus] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [formState, setFormState] = useState(initialValues)
  const [shouldFetchData, setShouldFetchData] = useState(false)
  const [approvedApplications, setApprovedApplications] = useState([])
  const [reprovedApplications, setReprovedApplications] = useState([])

  const loadingComponentProps = {
    loadingText: 'Carregando estatísticas...',
    layoutOrientation: 'vertical',
    textColor: Colors.primaryColor,
    progressSize: 64,
    textMargin: '20px',
    textSize: '20px',
    progressColor: Colors.primaryColor,
    minHeight: '65vh',
    fontWeight: '700',
    width: '100%',
  }

  const initializeStateFromData = (data, stateName, keysToPick) => {
    const selectedData = pick(data[stateName], keysToPick)

    if (Object.keys(selectedData).length !== keysToPick.length) {
      console.error(`failed to locate specified keys in data.${stateName}.`)
      return []
    }

    return Object.values(selectedData)
  }

  const initializeStates = (data) => {
    if (!data || !data.review_stats || !data.application_stats) {
      console.error('Dados inválidos. Não foi possível inicializar os estados.')
      return
    }

    setAnalysisData(data)

    const review_stats = { ...data.review_stats }
    review_stats.finished = data.review_stats.finished?.total
    setAnalysisStatus(Object.values(Object.keys(review_stats)
      .filter(key => key !== 'created')
      .reduce((obj, key) => {
        return { ...obj, [key]: review_stats[key] }
      }, {}),
    ))

    setFinishedStatus(
      initializeStateFromData(
        data.review_stats,
        'finished',
        ['approved', 'reproved'],
      ),
    )

    setApprovedApplications(
      initializeStateFromData(
        data.application_stats,
        'approved',
        ['auto', 'manual'],
      ),
    )

    setReprovedApplications(
      initializeStateFromData(
        data.application_stats,
        'reproved',
        ['auto', 'manual'],
      ),
    )
  }


  const fetchData = useCallback(() => {
    if (!formState.startDate || !formState.endDate || new Date(formState.startDate) > new Date(formState.endDate)) {
      toast.error('Verifique os dados informados')
      return
    }

    setShouldFetchData(true)

    const params = {
      params: {
        start_date: formState.startDate,
        end_date: formState.endDate,
      },
    }

    setIsLoading(true)

    getReviewAnalytics(token, params)
      .then((response) => {
        const { data } = response
        initializeStates(data)
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.detail) {
          toast.error(error.response.data.detail)
        } else {
          toast.error('Erro ao obter os dados do servidor. Por favor, tente novamente mais tarde.')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [token, formState.startDate, formState.endDate])

  const handleClick = () => {
    setShouldFetchData(true)
    fetchData()
  }

  const handleChangeFormValue = (event) => {
    const { name, value } = event.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <Container maxWidth='xl' sx={{ py: '3.5rem', fontSize: 18 }}>
      <Grid container spacing={3}>
        <Grid item sm={12}>
          <h2>Estatísticas</h2>
        </Grid>

        <Grid item sm={12}>
          <DashboardCard>
            <Grid
              container
              alignItems='flex-end'
              spacing={2}
            >
              <Grid item xs={12} sm='auto'>
                <TextField
                  fullWidth
                  variant='standard'
                  name='startDate'
                  label='Data inicial:'
                  type='date'
                  defaultValue={formState.startDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={handleChangeFormValue}
                />
              </Grid>
              <Grid item xs={12} sm='auto'>
                <TextField
                  fullWidth
                  variant='standard'
                  label='Data final:'
                  name='endDate'
                  type='date'
                  defaultValue={formState.endDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formState.startDate,
                  }}
                  onChange={handleChangeFormValue}
                />
              </Grid>
              <Grid item xs={12} sm='auto'>
                <Button fullWidth onClick={handleClick} loading={isLoading} disabled={isLoading}>
                  Buscar
                </Button>
              </Grid>

              <Grid item md={12} sm={12} xs={12}>
                <Alert severity='info' style={{ marginTop: '8px' }}>
                  Para obter métricas completas de um mês ou ano, certifique-se de especificar as datas de início e
                  término <b>exatas</b>. Por exemplo, início em 01/09/2023 e término em 30/09/2023.
                </Alert>
              </Grid>
            </Grid>
          </DashboardCard>
        </Grid>


        {isLoading ? (
          <LoadingComponent {...loadingComponentProps} />
        ) : (
          <Grid item sm={12}>
            <Grid container spacing={3}>
              <Grid item md={6} sm={12} xs={12}>
                <ApplicationApprovalChart 
                  startDate={formState.startDate}
                  endDate={formState.endDate}
                  shouldFetchData={shouldFetchData} 
                />
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <ApplicationApprovalByRiskChart
                  startDate={formState.startDate}
                  endDate={formState.endDate}
                  shouldFetchData={shouldFetchData}
                />
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <AssessorsResponseTimeSLAChart
                  startDate={formState.startDate}
                  endDate={formState.endDate}
                  shouldFetchData={shouldFetchData}
                />
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <ComplianceAnalysisSLAChart
                  startDate={formState.startDate}
                  endDate={formState.endDate}
                  shouldFetchData={shouldFetchData} 
                />
              </Grid>

              <Grid item md={4} sm={12}>
                <ReviewStatsSummary data={analysisData} title='Visão geral - Análises manuais' />
              </Grid>

              <Grid item md={4} sm={12} xs={12}>
                <DoughnutChart
                  data={analysisStatus}
                  labels={['Em andamento', 'Encaminhadas', 'Suspensas', 'Finalizadas']}
                  title='Análises por status'
                  colors={chartColors.slaAnalytics}
                />
              </Grid>

              <Grid item md={4} sm={12} xs={12}>
                <DoughnutChart
                  data={finishedStatus}
                  labels={['Aprovadas', 'Reprovadas']}
                  title='Análises Finalizadas'
                  colors={chartColors.finishedReviews}
                />
              </Grid>

              <Grid item md={4} sm={12}>
                <ApplicationStatsSummary data={analysisData} title='Visão geral - Aplicações' />
              </Grid>

              <Grid item md={4} sm={12} xs={12} sx={{
                flexGrow: 1,
              }}>
                <DoughnutChart
                  data={approvedApplications}
                  labels={['Automaticamente', 'Manualmente']}
                  title='Aplicações aprovadas'
                  colors={chartColors.approvedApplications}
                />
              </Grid>

              <Grid item md={4} sm={12} xs={12}>
                <DoughnutChart
                  data={reprovedApplications}
                  labels={['Automaticamente', 'Manualmente']}
                  title='Aplicações reprovadas'
                  colors={chartColors.reprovedApplications}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Container>
  )
}


export default Statistics
